/**
 * SkillsTrendPanel (#9 Skills-trend radar) — welke skills stijgen in vraag in
 * vacatures binnen jouw niche/regio (Perplexity live web-search, wekelijkse cache).
 * Klik op een skill → toegevoegd aan de zoekopdracht (JD-veld) via onAddSkill.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { TrendingUp, ChevronDown, ChevronUp, Loader2, Flame, Plus } from "lucide-react";

export default function SkillsTrendPanel({ onAddSkill }) {
    const [doc, setDoc] = useState(null);
    const [open, setOpen] = useState(false);
    const [running, setRunning] = useState(false);
    const [niche, setNiche] = useState("");
    const [region, setRegion] = useState("België");

    useEffect(() => {
        api.get("/radar/skills-trends").then((r) => {
            setDoc(r.data.trends);
            if (r.data.trends?.niche) setNiche(r.data.trends.niche);
            if (r.data.trends?.region) setRegion(r.data.trends.region);
        }).catch(() => {});
    }, []);

    const run = async () => {
        if (niche.trim().length < 2) { toast.error(i18n.t("skilltrends.niche_required")); return; }
        setRunning(true);
        try {
            const r = await api.post("/radar/skills-trends/run", { niche: niche.trim(), region: region.trim() || "België" });
            setDoc(r.data.trends);
            toast.success(i18n.t("skilltrends.run_ok", { n: (r.data.trends?.items || []).length }));
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("skilltrends.run_failed")); }
        finally { setRunning(false); }
    };

    const age = doc?.generated_at
        ? Math.floor((Date.now() - new Date(doc.generated_at).getTime()) / 86400000)
        : null;

    return (
        <div className="mt-4 rounded-xl border border-white/10 bg-[var(--jr-surface)]" data-testid="skills-trend-panel">
            <button onClick={() => setOpen(!open)} data-testid="skilltrends-toggle"
                className="w-full flex items-center gap-3 px-4 py-3 text-left">
                <TrendingUp size={15} className="text-[#22c55e]" />
                <span className="text-sm font-medium text-white">{i18n.t("skilltrends.title")}</span>
                {doc && (
                    <span className="text-[10px] text-[var(--jr-text-3)] font-mono">
                        {doc.niche} · {doc.region}{age != null ? ` · ${i18n.t("skilltrends.age", { n: age })}` : ""}
                    </span>
                )}
                <span className="ml-auto text-[var(--jr-text-3)]">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
            </button>
            {open && (
                <div className="px-4 pb-4">
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                        <input value={niche} onChange={(e) => setNiche(e.target.value)}
                            placeholder={i18n.t("skilltrends.niche_placeholder")}
                            data-testid="skilltrends-niche"
                            className="flex-1 min-w-[180px] bg-[var(--jr-surface-2)] border border-white/10 rounded px-2.5 py-1.5 text-sm text-white outline-none focus:border-[#22c55e]" />
                        <input value={region} onChange={(e) => setRegion(e.target.value)}
                            data-testid="skilltrends-region"
                            className="w-32 bg-[var(--jr-surface-2)] border border-white/10 rounded px-2.5 py-1.5 text-sm text-white outline-none focus:border-[#22c55e]" />
                        <button onClick={run} disabled={running} data-testid="skilltrends-run"
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#22c55e]/50 text-[#22c55e] hover:bg-[#22c55e]/10 font-medium disabled:opacity-50">
                            {running ? <Loader2 size={12} className="animate-spin" /> : <TrendingUp size={12} />}
                            {running ? i18n.t("skilltrends.running") : i18n.t("skilltrends.run_btn")}
                        </button>
                    </div>
                    {!doc ? (
                        <p className="text-xs text-[var(--jr-text-3)]">{i18n.t("skilltrends.empty")}</p>
                    ) : (
                        <div className="space-y-2">
                            {(doc.items || []).map((s, i) => (
                                <div key={i} className="flex items-start gap-3 rounded-lg border border-white/10 bg-[var(--jr-surface-2)] px-3 py-2"
                                    data-testid={`skilltrend-item-${i}`}>
                                    <span className={`mt-0.5 inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full border ${s.trend === "hot"
                                        ? "text-[#f87171] border-[#f87171]/40" : s.trend === "rising"
                                            ? "text-[#22c55e] border-[#22c55e]/40" : "text-[var(--jr-text-3)] border-white/15"}`}>
                                        {s.trend === "hot" ? <Flame size={10} /> : <TrendingUp size={10} />} {s.trend}
                                    </span>
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-white">{s.skill}</div>
                                        <div className="text-[11px] text-[var(--jr-text-3)]">{s.reason}</div>
                                        {s.related_roles && <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5 font-mono">→ {s.related_roles}</div>}
                                    </div>
                                    {onAddSkill && (
                                        <button onClick={() => { onAddSkill(s.skill); toast.success(i18n.t("skilltrends.added", { skill: s.skill })); }}
                                            data-testid={`skilltrend-add-${i}`}
                                            title={i18n.t("skilltrends.add_tip")}
                                            className="ml-auto shrink-0 inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-white/15 text-[var(--jr-text-2)] hover:text-white hover:border-[#22c55e]/50">
                                            <Plus size={11} /> {i18n.t("skilltrends.add_btn")}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
